import { ACTION_CLICK_PLATTER_CARD } from 'utils/analytics/eventTypeConstantsAmplitude';
import { BaseWidgetProps } from 'consumer-page-widgets/WidgetUtils/BaseWidgetProps';
import { HorizontalScrollContainer, HorizontalScrollContainerProps, Typography } from 'omakase';
import { StoreType } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { WidgetCreatorContentProps } from 'components/www/WidgetCreatorContent';
import { consumer, core } from '@tock/coretypes';
import { currentMetroAreaSelector } from 'selectors/metroAreaSelectors';
import { getExplorePlatterCards } from 'routes/www/explorePlatterCards';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useSelector } from 'react-redux';
import PlatterCard, { PlatterCardProps } from 'omakase/PlatterCard/PlatterCard';
import React, { FunctionComponent } from 'react';

interface ExplorePlatterWidgetScrollableProps {
  pictureCards: PlatterCardProps[];
}

const ExplorePlatterWidgetScrollable: FunctionComponent<
  ExplorePlatterWidgetScrollableProps & HorizontalScrollContainerProps
> = ({ pictureCards, ...rest }) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  return (
    <HorizontalScrollContainer
      {...rest}
      responsiveCardCount={{
        xs: 1.5,
        sm: 2,
        md: 3,
        lg: pictureCards.length,
        xl: pictureCards.length,
      }}
      buttonVariant={'ghost'}
    >
      {pictureCards.map((pictureCard, index) => (
        <PlatterCard
          key={`${index}-offerings-platter-card-key`}
          {...pictureCard}
          onClick={() =>
            amplitudeSendEvent(ACTION_CLICK_PLATTER_CARD, {
              title: pictureCard.title,
              listIndex: index,
              pageWidgetListIndex: 0,
              widgetName: 'explore platter',
              widgetType: 'Feature carousel widget',
            })
          }
        />
      ))}
    </HorizontalScrollContainer>
  );
};

interface ConnectProps {
  metroArea: core.MetroArea | undefined;
}

interface OfferingsPlatterWidgetProps extends BaseWidgetProps {
  widget: consumer.IOfferingsPlatterWidget;
  widgetInfo?: consumer.IWidgetInfo;
  options: WidgetCreatorContentProps['options'];
  pageWidgetListIndex?: number;
}

const OfferingsPlatterWidget: React.FunctionComponent<OfferingsPlatterWidgetProps> = ({
  widget,
  widgetInfo,
  options,
  pageWidgetListIndex,
}) => {
  const { metroArea } = useSelector(mapStateToProps);
  const isHomepageThanksgivingPlatterEnabled = useFeatureFlagForConsumer(
    'HOMEPAGE_PLATTER_THANKSGIVING'
  );
  const isHomepageHolidayPlatterEnabled = useFeatureFlagForConsumer('HOMEPAGE_PLATTER_HOLIDAYS');

  const mode = isHomepageThanksgivingPlatterEnabled
    ? 'holidayThanksgiving'
    : isHomepageHolidayPlatterEnabled
      ? 'holidayRest'
      : 'default';

  return (
    <div
      css={({ breakpoints, spacing }: Theme) => css`
        margin-bottom: -40px;

        ${breakpoints.up('sm')} {
          padding-left: ${spacing(2.5)}px;
          padding-right: ${spacing(2.5)}px;
        }
        ${breakpoints.up('md')} {
          padding-left: ${spacing(7.5)}px;
          padding-right: ${spacing(7.5)}px;
        }
      `}
    >
      <div
        css={css`
          max-width: 1440px;
          margin-left: auto;
          margin-right: auto;
        `}
      >
        <div
          css={({ breakpoints: { up }, spacing }: Theme) => css`
            padding-top: 2px;
            ${up('sm')} {
              // If we apply any negative margin-right at medium width, this causes any page with this widget
              // on it to be slightly scrollable left/right. Don't apply that until large width.
              margin-left: ${spacing(-7.5)}px;
            }
            ${up('md')} {
              margin-right: ${spacing(-7.5)}px;
            }
          `}
        >
          <ExplorePlatterWidgetScrollable
            pictureCards={getExplorePlatterCards({
              metroArea,
              mode,
            })}
            heading={
              <Typography
                component={'h2'}
                variant="heading02"
                color={'fgDefault'}
                css={(theme: Theme) => css`
                  ${theme.breakpoints.up('md')} {
                    margin-bottom: ${theme.spacing(1.5)}px;
                  }
                `}
              >
                Delicious starts here
              </Typography>
            }
            responsiveCardCount={{ xs: 1.5, sm: 2, md: 4, lg: 6, xl: 6 }}
            css={(theme: Theme) => css`
              background-color: ${theme.colors.bgDefault};
              padding-top: 48px;
              margin-bottom: 0;
            `}
          />
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state: StoreType): ConnectProps {
  return {
    metroArea: currentMetroAreaSelector(state),
  };
}

export default OfferingsPlatterWidget;
